import React, { useCallback, useMemo } from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import DeleteIcon from '@mui/icons-material/Clear';
import Error from '@mui/icons-material/Error';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { MAIN_THEME } from '@styles';

import { AmendmentPolicyCoverForm } from '../types/getAmendmentsDetailsResponse.types';

import { FormikErrors, FormikTouched } from 'formik';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '14px',
    position: 'relative'
  },
  row: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '28.5% 22.5% 22.5% 22.5%'
  },
  rowTwoCols: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr 1fr'
  },
  rowSingleCol: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: '1fr'
  },
  removeBtn: {
    position: 'absolute',
    top: 6,
    right: 6
  },
  errorIconStyle: {
    height: '15px',
    width: '15px',
    color: 'red',
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    cursor: 'pointer'
  }
}));

type Props = {
  row: AmendmentPolicyCoverForm;
  rowIndex: number;

  disabledForm: boolean;
  allowPlanCoverEdits: boolean;
  amendmentStatus: string;
  currencySymbol: string;

  policyEffectiveDate?: string;
  policyExpiryDate?: string;
  amendmentEffectiveDate?: string;

  selectOptions: Record<string, string>;
  disablePolicyCoverSelect: boolean;
  onRemove?: () => void;

  touched: FormikTouched<{ covers: AmendmentPolicyCoverForm[] }>;
  errors: FormikErrors<{ covers: AmendmentPolicyCoverForm[] }>;

  setFieldValue: (path: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (
    path: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void;
  handleBlur: (fieldOrEvent: any) => void;
  onBlurPolicyCover: (selectedId?: string) => void;

  isLine51?: boolean;
  isGroup?: boolean;
  isCancellation?: boolean;

  // Formik dirty state for coverPremiumVariance field
  isPremiumVarianceDirty?: boolean;
};

const YES_NO_OPTIONS = { Yes: 'Yes', No: 'No' } as const;

const CancellationPolicyCoverRowAtom: React.FC<Props> = ({
  row,
  rowIndex,

  disabledForm,
  allowPlanCoverEdits,
  amendmentStatus,
  currencySymbol,

  policyEffectiveDate,
  policyExpiryDate,
  amendmentEffectiveDate,

  selectOptions,
  disablePolicyCoverSelect,
  onRemove,

  touched,
  errors,

  setFieldValue,
  setFieldTouched,
  handleBlur,
  onBlurPolicyCover,
  isLine51,
  isGroup,
  isCancellation,
  isPremiumVarianceDirty = false
}) => {
  const { classes } = useStyles();
  const base = `covers[${rowIndex}]`;

  // Dynamic row message function using Formik's dirty state
  const getDynamicRowMessage = useCallback((): string[] => {
    const messages: string[] = [];

    if (isCancellation && isPremiumVarianceDirty) {
      messages.push('Premium Variance has been changed.');
    }

    return messages;
  }, [isCancellation, isPremiumVarianceDirty]);

  const canToggleRemoved = useMemo(
    () => !row.isMandatory && !row.isNew && !isCancellation,
    [row.isMandatory, row.isNew, isCancellation]
  );

  const disabledRow = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    // For cancellations, most fields should be read-only
    if (isCancellation) {
      return true;
    }

    return disabledForm || isClosed || !allowPlanCoverEdits || !row.isEditable;
  }, [
    disabledForm,
    amendmentStatus,
    allowPlanCoverEdits,
    row.isEditable,
    isCancellation
  ]);

  const disabledIsRemoved = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    return disabledForm || isClosed || !allowPlanCoverEdits;
  }, [disabledForm, amendmentStatus, allowPlanCoverEdits]);

  const disabledPremiumVariance = useMemo(() => {
    const isClosed = ['COMPLETED', 'CANCELLED', 'CANCELED'].includes(
      (amendmentStatus || '').toUpperCase()
    );

    return disabledForm || isClosed || !allowPlanCoverEdits || !row.isEditable;
  }, [disabledForm, amendmentStatus, allowPlanCoverEdits, row.isEditable]);

  const showNewSection = !row.isNew;

  const handleRemovedToggle = useCallback(
    async (value: 'YES' | 'NO') => {
      const isRemoved = value === 'YES';
      await setFieldValue(`${base}.isRemoved`, isRemoved, false);
      await setFieldTouched(`${base}.isRemoved`, true, false);

      if (isRemoved && canToggleRemoved) {
        await setFieldValue(
          `${base}.coverEffectiveTo`,
          amendmentEffectiveDate,
          false
        );

        const calculateProRataForRemoved = () => {
          const d = (s?: string) => (s ? new Date(s).getTime() : NaN);
          const to = d(amendmentEffectiveDate);
          const from = d(row.coverEffectiveFrom);
          const pTo = d(policyExpiryDate);
          const pFrom = d(policyEffectiveDate);

          if (
            Number.isNaN(to) ||
            Number.isNaN(from) ||
            Number.isNaN(pTo) ||
            Number.isNaN(pFrom)
          )
            return 0;

          const top = Math.max(0, to - from);
          const bottom = Math.max(1, pTo - pFrom);
          const ratio = +(top / bottom);

          return ratio;
        };

        const pr = calculateProRataForRemoved();

        await setFieldValue(`${base}.proRataRatio`, pr, false);

        const round3 = (n?: number) =>
          typeof n === 'number' && !Number.isNaN(n) ? +n.toFixed(3) : undefined;

        const newPremiumAmountComputed =
          row.coverPremiumType === 'AMOUNT'
            ? row.coverPremiumAmount
              ? (row.coverPremiumAmount * pr).toFixed(3)
              : undefined
            : undefined;

        await setFieldValue(
          `${base}.newPremiumAmount`,
          newPremiumAmountComputed,
          false
        );

        await setFieldValue(
          `${base}.newPremiumPercentage`,
          row.coverPremiumType === 'PERCENTAGE'
            ? row.coverPremiumPercentage
            : undefined,
          false
        );
      } else if (!isRemoved && canToggleRemoved) {
        await setFieldValue(
          `${base}.coverEffectiveTo`,
          policyExpiryDate,
          false
        );

        await setFieldValue(`${base}.proRataRatio`, undefined, false);

        await setFieldValue(
          `${base}.newPremiumAmount`,
          row.coverPremiumType === 'AMOUNT'
            ? row.coverPremiumAmount
            : undefined,
          false
        );

        await setFieldValue(
          `${base}.newPremiumPercentage`,
          row.coverPremiumType === 'PERCENTAGE'
            ? row.coverPremiumPercentage
            : undefined,
          false
        );
      }
    },
    [
      base,
      setFieldValue,
      setFieldTouched,
      canToggleRemoved,
      amendmentEffectiveDate,
      policyExpiryDate,
      policyEffectiveDate,
      row.coverEffectiveFrom,
      row.coverPremiumType,
      row.coverPremiumAmount,
      row.coverPremiumPercentage,
      rowIndex
    ]
  );

  const err = (key: keyof AmendmentPolicyCoverForm) =>
    (touched?.covers?.[rowIndex] as any)?.[key]
      ? (errors?.covers?.[rowIndex] as any)?.[key]
      : '';

  const renderRowMessage = () => {
    const messages = getDynamicRowMessage();
    if (messages && messages.length > 0) {
      return (
        <Tooltip title={messages[0]}>
          <Error className={classes.errorIconStyle} />
        </Tooltip>
      );
    }

    return null;
  };
  console.log(row.coverPremiumAmount);

  return (
    <div className={classes.container}>
      {row.isNew && !row.isMandatory && onRemove && (
        <Tooltip title="Remove Policy Plan Cover">
          <IconButton
            size="small"
            className={classes.removeBtn}
            onClick={onRemove}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Section 1 / Row 1 - Hide isMain for cancellations */}
      <div className={classes.row}>
        <SelectFormField
          name={`${base}.policyCover`}
          title="Policy Cover*"
          placeholder="Policy Cover"
          value={row.policyCover}
          selectOptions={selectOptions}
          onChange={() => { }}
          disabled
          material
        />
        <SelectFormField
          name={`${base}.isMandatory`}
          title="Is Mandatory*"
          placeholder="Is Mandatory"
          value={row.isMandatory ? 'Yes' : 'No'}
          disabled
          selectOptions={YES_NO_OPTIONS}
          material
        />
      </div>
      <div className={classes.row}>
        <DatePickerFormField
          name={`${base}.coverEffectiveFrom`}
          title="Cover Effective From*"
          placeholder="Effective From"
          value={row.coverEffectiveFrom}
          onDateChange={() => { }}
          disabled
          material
        />
        <DatePickerFormField
          name={`${base}.coverEffectiveTo`}
          title="Cover Effective To*"
          placeholder="Effective To"
          value={row.coverEffectiveTo}
          onDateChange={() => { }}
          disabled
          material
        />
        <SelectFormField
          name={`${base}.sumInsuredIsAdditive`}
          title="Additive Sum Insured*"
          placeholder="Additive Sum Insured"
          value={row.sumInsuredIsAdditive ? 'Yes' : 'No'}
          disabled
          selectOptions={YES_NO_OPTIONS}
          material
        />

        {!isGroup && <CurrencyFormField
          name={`${base}.coverSumInsured`}
          title="Sum Insured*"
          placeholder="Sum Insured"
          value={row.coverSumInsured}
          onChange={() => { }}
          disabled
          material
          currencySymbol={currencySymbol}
        />}
      </div>

      <div className={classes.row}>
        {/* Show Premium Type only for line 51 or non-cancellation */}
        {isLine51 && !isGroup && (
          <SelectFormField
            name={`${base}.coverPremiumType`}
            title="Premium Type*"
            placeholder="Premium Type"
            value={row.coverPremiumType}
            disabled
            selectOptions={{
              AMOUNT: 'Amount',
              PERCENTAGE: 'Percentage'
            }}
            material
          />
        )}
        {row.coverPremiumType === 'AMOUNT' && !isGroup && (
          <CurrencyFormField
            name={`${base}.coverPremiumAmount`}
            title="Premium*"
            placeholder="Premium Amount"
            value={row.coverPremiumAmount ?? ''}
            onChange={() => { }}
            disabled
            material
            currencySymbol={currencySymbol}
          />
        )}
        {row.coverPremiumType === 'PERCENTAGE' && !isGroup && (
          <PercentageFormField
            name={`${base}.coverPremiumPercentage`}
            title="Premium Percentage*"
            placeholder="Premium Percentage"
            value={row.coverPremiumPercentage}
            onChange={() => { }}
            disabled
            material
          />
        )}
      </div>
      {/* Premium Variance field for cancellation scenarios */}
      {!isGroup && <div className={classes.row} style={{ marginTop: 8 }}>
        <div style={{ position: 'relative' }}>
          <CurrencyFormField
            name={`${base}.coverPremiumVariance`}
            title="Premium Variance*"
            placeholder="Premium Variance"
            value={(row as any).coverPremiumVariance}
            error={err('coverPremiumVariance' as any)}
            onBlur={(e) => handleBlur(e)}
            onChange={(e) => {
              setFieldValue(
                `${base}.coverPremiumVariance`,
                e.target.value,
                false
              );
            }}
            disabled={disabledPremiumVariance}
            material
            currencySymbol={currencySymbol}
            allowNegative={true}
            minValue={
              row.coverPremiumAmount ? -1 * row.coverPremiumAmount : undefined
            }
            maxValue={0}
          />
          {renderRowMessage()}
        </div>
      </div>}
    </div>
  );
};

export { CancellationPolicyCoverRowAtom };
